import { FilterOperations } from '@/utils/filter';

export type FilterOperation = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'like' | 'in' | 'ilike';

export interface IFilterExpression {
  value?: string[] | string;
  operation: FilterOperation;
  field: string;
}

export interface IFilter {
  [field: string]: IFilterExpression;
}

const conditions = {
  contains: {
    text: 'Contains',
    mapsTo: 'like',
  },
  notContains: {
    text: 'Not contains',
    mapsTo: 'nlike',
  },
  equals: {
    text: 'Equals',
    mapsTo: 'eq',
  },
  notEqual: {
    text: 'Not equal',
    mapsTo: 'neq',
  },
  startsWith: {
    text: 'Starts with',
    mapsTo: 'startWith',
  },
  endsWith: {
    text: 'Ends with',
    mapsTo: 'endWith',
  },
  blank: {
    text: 'Blank',
    mapsTo: 'eq',
  },
  notBlank: {
    text: 'Not blank',
    mapsTo: 'neq',
  },
  empty: {
    text: 'Choose one',
  },
  lessThan: {
    text: 'Less than',
    mapsTo: 'lt',
  },
  lessThanOrEqual: {
    text: 'Less than or equals',
    mapsTo: 'lte',
  },
  greaterThan: {
    text: 'Greater than',
    mapsTo: 'gt',
  },
  greaterThanOrEqual: {
    text: 'Greater than or equals',
    mapsTo: 'gte',
  },
  inRange: {
    text: 'In range',
    mapsTo: 'inrange',
  },
  in: {
    text: 'In',
    mapsTo: 'like',
  },
  notIn: {
    text: 'Not in',
    mapsTo: 'nlike',
  },
};
const formatValue = (value) => {
  if (Array.isArray(value)) {
    return value.join(',');
  }
  return value;
};

/**
 * @param {fieldName} fieldName
 * @param {IFilterExpression} filterExpression
 * @returns {string}
 */
const convertFilterExpressionToString = (fieldName, filterExpression) => {
  const filterValue = formatValue(filterExpression.value);
  if (filterExpression.operation === 'blank') {
    return `${fieldName}=eq:""`;
  }
  if (filterExpression.operation === 'notBlank') {
    return `${fieldName}=neq:""`;
  }
  if (!filterValue || filterValue === '') {
    return '';
  }
  if (!conditions[filterExpression.operation] || !conditions[filterExpression.operation].mapsTo) {
    return '';
  }
  return `${fieldName}=${conditions[filterExpression.operation].mapsTo}:${filterValue}`;
};

/**
 * @param {IFilter} filter
 * @returns {string}
 */
const serializeFilter = (filter) => {
  const filterQueryParams = Object.keys(filter)
    .map((fieldName) => convertFilterExpressionToString(fieldName, filter[fieldName]))
    .filter((x) => x)
    .join('&');
  if (!filterQueryParams) {
    return '';
  }
  return `?${filterQueryParams}`;
};

const getFilterEmptyValue = (operation) => {
  if (operation === FilterOperations.In) {
    return [];
  }
  return '';
};

const clearAllFilters = (filter) => {
  const newFilter = { ...filter };
  Object.values(newFilter).forEach((filterValue: any) => {
    // eslint-disable-next-line no-param-reassign
    filterValue.value = getFilterEmptyValue(filterValue.operation);
  });
  return newFilter;
};

/** applies a filter on the client */
export function applyFilter(items, filter) {
  const fields = Object.keys(filter);
  return items.filter((item) => fields.every((field) => {
    let { value } = filter[field];
    const { operation } = filter[field];
    let itemValue = item[field] != null ? String(item[field]) : item[field];

    if (value === '' && operation !== 'blank' && operation !== 'notBlank') {
      return true; // Skip this filter if the value is empty and it's not blank or notBlank
    }

    if (typeof value === 'string') {
      value = value.toLowerCase();
    }

    if (typeof itemValue === 'string' && operation !== 'in') {
      itemValue = itemValue.toLowerCase();
    }

    switch (operation) {
      case 'contains':
        return typeof itemValue === 'string' && itemValue.includes(value);
      case 'notContains':
        return typeof itemValue === 'string' && !itemValue.includes(value);
      case 'equals':
        return itemValue === value;
      case 'notEqual':
        return itemValue !== value;
      case 'startsWith':
        return typeof itemValue === 'string' && itemValue.startsWith(value);
      case 'endsWith':
        return typeof itemValue === 'string' && itemValue.endsWith(value);
      case 'blank':
        return itemValue === null || itemValue === undefined || itemValue === '';
      case 'notBlank':
        return itemValue !== null && itemValue !== undefined && itemValue !== '';
      case 'in':
        return Array.isArray(value) && value.includes(itemValue);
      default:
        return true;
    }
  }));
}

export {
  serializeFilter, clearAllFilters, getFilterEmptyValue,
};
